import React, { ReactElement, Ref } from 'react';

import PropTypes from 'prop-types';
import { CSSProp } from 'styled-components';

import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import Token from './Token';
import { GenericItem, GetSelectedItemProps, ItemToString } from '../../types';

export type TokensProps<TItem extends GenericItem> =
  StylableComponent<HTMLDivElement> & {
    selectedItems: TItem[];
    getSelectedItemProps: GetSelectedItemProps<TItem>;
    removeSelectedItem: (item: TItem) => void;
    itemToString: ItemToString<TItem>;
  };

const TokensWrapped = <TItem extends GenericItem>(
  {
    className,
    style,
    selectedItems,
    getSelectedItemProps,
    removeSelectedItem,
    itemToString,
    ...props
  }: TokensProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  return (
    <div
      ref={ref}
      className={className}
      css={[
        {
          display: 'flex',
          flexWrap: 'wrap',
          flex: 1,
          overflowY: 'auto',
          maxHeight: 'inherit',
        },
        style,
      ]}
      {...props}
    >
      {selectedItems.map((selectedItem, index) => {
        const {
          style: tokenStyle,
          css: _ignored,
          ...selectedItemProps
        } = getSelectedItemProps({
          selectedItem,
          index,
        });

        return (
          <Token
            key={`selected-item-${index}`}
            style={tokenStyle as CSSProp}
            onClickX={() => removeSelectedItem(selectedItem)}
            {...selectedItemProps}
            content={itemToString(selectedItem)}
          />
        );
      })}
    </div>
  );
};

const Tokens = React.forwardRef(TokensWrapped);

Tokens.displayName = 'Tokens';

Tokens.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Array of selected items to represent with Tokens */
  selectedItems: PropTypes.array.isRequired,
  /** Function that returns props which will be applied to each Token */
  getSelectedItemProps: PropTypes.func.isRequired,
  /** Function executed upon clicking the X icon of a Token */
  removeSelectedItem: PropTypes.func.isRequired,
  /** Function which converts an item to its string representation */
  itemToString: PropTypes.func.isRequired,
};

export default Tokens as <T extends GenericItem>(
  p: TokensProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;
